import { createContext, type KeyboardEvent, type ReactElement, type ReactNode, useContext, useRef } from 'react';

/**
 * Small structural primitives the apps used to hand-roll (V-LIB-FORMS, D508): `Tabs` (an accessible tablist),
 * `KeyValue` / `KeyValueList` (label and value rows) and `SectionTitle` (the heading of a panel section).
 */

// ---- Tabs ---------------------------------------------------------------------------------------------

export interface TabSpec<T extends string> {
  readonly id: T;
  readonly label: ReactNode;
  /** A count shown after the label, in the mono face (`"12"`). */
  readonly count?: string;
  readonly disabled?: boolean;
}

export interface TabsProps<T extends string> {
  /** Base id: tab `x` gets `${id}-tab-x` and controls `${id}-panel-x` (what {@link TabPanel} renders). */
  readonly id: string;
  readonly tabs: readonly TabSpec<T>[];
  readonly value: T;
  readonly onChange: (id: T) => void;
  /** The tablist's accessible name. */
  readonly ariaLabel: string;
  readonly className?: string;
}

/**
 * An accessible tablist (WAI-ARIA tabs pattern, automatic activation): the selected tab is the one in the tab order,
 * ←/→ move to and select the previous / next enabled tab (wrapping), Home / End go to the first / last. Render the
 * content with {@link TabPanel}.
 */
export function Tabs<T extends string>({ id, tabs, value, onChange, ariaLabel, className }: TabsProps<T>): ReactElement {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  const enabled = tabs.filter((t) => t.disabled !== true);
  const move = (to: TabSpec<T> | undefined): void => {
    if (to === undefined) return;
    onChange(to.id);
    refs.current.get(to.id)?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const at = enabled.findIndex((t) => t.id === value);
    if (at < 0) return;
    if (e.key === 'ArrowRight') move(enabled[(at + 1) % enabled.length]);
    else if (e.key === 'ArrowLeft') move(enabled[(at - 1 + enabled.length) % enabled.length]);
    else if (e.key === 'Home') move(enabled[0]);
    else if (e.key === 'End') move(enabled[enabled.length - 1]);
    else return;
    e.preventDefault();
  };
  return (
    <div className={`vello-tabs${className !== undefined ? ` ${className}` : ''}`} role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      {tabs.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            ref={(el) => {
              if (el === null) refs.current.delete(t.id);
              else refs.current.set(t.id, el);
            }}
            type="button"
            role="tab"
            id={`${id}-tab-${t.id}`}
            className="vello-tab"
            aria-selected={selected}
            aria-controls={`${id}-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            disabled={t.disabled}
            onClick={() => !selected && onChange(t.id)}
          >
            {t.label}
            {t.count !== undefined && <span className="vello-tab__count tnum">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The panel a tab controls. Renders only while its tab is the selected one. */
export function TabPanel<T extends string>({ id, tab, value, children }: { id: string; tab: T; value: T; children: ReactNode }): ReactElement | null {
  if (tab !== value) return null;
  return (
    <div role="tabpanel" className="vello-tabpanel" id={`${id}-panel-${tab}`} aria-labelledby={`${id}-tab-${tab}`} tabIndex={0}>
      {children}
    </div>
  );
}

// ---- KeyValue -----------------------------------------------------------------------------------------

// Inside a `KeyValueList` a row is a real <dt>/<dd> pair; on its own (a row among other content) it has no list to belong to,
// so it renders neutral spans — a <dt> outside a <dl> is invalid markup.
const InList = createContext(false);

/**
 * One label / value row of a record. The value is the children, or the `value` prop (the older call shape). `mono` puts the
 * value (a number, a code, an id, a time) in the tabular mono face.
 */
export function KeyValue({ label, children, value, mono = false }: { label: ReactNode; children?: ReactNode; value?: ReactNode; mono?: boolean }): ReactElement {
  const inList = useContext(InList);
  const content = children ?? value;
  const valueCls = `vello-kv__value${mono ? ' tnum' : ''}`;
  if (inList) {
    return (
      <div className="vello-kv">
        <dt className="vello-kv__key">{label}</dt>
        <dd className={valueCls}>{content}</dd>
      </div>
    );
  }
  return (
    <div className="vello-kv">
      <span className="vello-kv__key">{label}</span>
      <span className={valueCls}>{content}</span>
    </div>
  );
}

export interface KeyValueItem {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly mono?: boolean;
}

/** A definition list of {@link KeyValue} rows — pass `items`, or `KeyValue` children. `dense` tightens the rows. */
export function KeyValueList({ items, children, dense = false }: { items?: readonly KeyValueItem[]; children?: ReactNode; dense?: boolean }): ReactElement {
  return (
    <InList.Provider value>
      <dl className={`vello-kvlist${dense ? ' vello-kvlist--dense' : ''}`}>
        {items?.map((item, i) => (
          <KeyValue key={i} label={item.label} mono={item.mono ?? false}>
            {item.value}
          </KeyValue>
        ))}
        {children}
      </dl>
    </InList.Provider>
  );
}

// ---- SectionTitle -------------------------------------------------------------------------------------

/**
 * The heading of a section inside a panel. `level` is the heading level (default 2); `size="sub"` draws the smaller
 * sub-section style (body size, label weight). `actions` puts controls at the end of the same row.
 */
export function SectionTitle({ children, level = 2, size = 'title', actions }: { children: ReactNode; level?: 2 | 3 | 4; size?: 'title' | 'sub'; actions?: ReactNode }): ReactElement {
  const Heading = `h${level}` as 'h2' | 'h3' | 'h4';
  const heading = <Heading className={`vello-section-title${size === 'sub' ? ' vello-section-title--sub' : ''}`}>{children}</Heading>;
  if (actions === undefined) return heading;
  return (
    <div className="vello-section-head">
      {heading}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>{actions}</div>
    </div>
  );
}
